// Écran 3 — Constats et scénarios : chemins d'attaque, pertes potentielles estimées en euros, détail du calcul du moteur
// déterministe. Vocabulaire : docs/REGLE-EDITORIALE.md (les noms techniques des champs ne changent pas).
import { ReserveCalcul, ReserveCourte } from '../composants/ManquesCalcul';
import type { Calcul, ObjetConstat, Scenario } from '../api/types';
import { Attendre, BlocProvenance, Etiquette, EtiquetteGravite } from '../composants/communs';
import { useCalcul, useCarte, useConstats, useEkera } from '../contexte';
import { EtatVide } from '../composants/EtatVide';
import { carteVide, indexer, nomDe, type IndexCarte } from '../domaine/carte';
import {
  decimal,
  eurosCourt,
  eurosLong,
  fourchette,
  LIBELLE_NATURE_CONSTAT,
  LIBELLE_STATUT_NIS2,
  pourcent,
  valeurLisible,
} from '../domaine/format';
import { contributionAnnuelle, libelleNis2, postes, sommePostesComptes, trierParPerte, verifierPerteAnnuelle } from '../domaine/scenarios';
import { lienVers, naviguer } from '../routeur';
import { Fournisseurs } from '../composants/Fournisseurs';
import { LienRetour } from '../composants/LienRetour';

export function EcranConstats({ params }: { params: URLSearchParams }) {
  const carte = useCarte();
  const calcul = useCalcul();
  const constats = useConstats();
  // Carte vide : rien à calculer encore, on le dit au lieu d'afficher des zéros (ou l'erreur d'un calcul sans objet).
  if (carte.data && carteVide(carte.data))
    return (
      <EtatVide
        titre="Aucun constat ni scénario pour l’instant"
        idTitre="t-constats-vide"
        texte={[
          'Les constats et les scénarios chiffrés se calculent sur la carte, et la carte est encore vide. Après la lecture de vos pièces, Ekera montrera ce qu’une attaque par chaque porte d’entrée ou la panne d’un point unique arrêterait, et les pertes potentielles estimées, conditionnelles aux hypothèses retenues.',
          'Les montants viennent d’un moteur de calcul déterministe, à partir de vos comptes (le fichier des écritures comptables aide) ; ce sont des estimations, pas des prévisions.',
        ]}
      />
    );
  return (
    <Attendre etats={[carte, calcul, constats]} quoi="des scénarios">
      {() => (
        <Vue ix={indexer(carte.data!)} calcul={calcul.data!} constats={constats.data!.constats} manquesConstats={constats.data!.manques} params={params} />
      )}
    </Attendre>
  );
}

function Vue({ ix, calcul, constats, manquesConstats, params }: { ix: IndexCarte; calcul: Calcul; constats: ObjetConstat[]; manquesConstats?: Calcul['manques']; params: URLSearchParams }) {
  const tries = trierParPerte(calcul.scenarios);
  const idScenario = params.get('scenario') ?? (params.get('constat') ? null : tries[0]?.id ?? null);
  const scenario = calcul.scenarios.find((s) => s.id === idScenario) ?? null;
  const idConstat = params.get('constat');
  const constat = constats.find((c) => c.id === idConstat) ?? null;
  const nom = (id: string) => nomDe(ix, id);
  const verif = verifierPerteAnnuelle(calcul);
  const triConstats = [...constats].sort(
    (a, b) => (b.attributs?.perte_estimee?.haut.valeur ?? 0) - (a.attributs?.perte_estimee?.haut.valeur ?? 0),
  );
  return (
    <div className="pile">
      <p className="sous-titre">
        Le moteur déterministe d’Ekera calcule, sur la carte, ce qu’un attaquant atteint depuis chaque porte d’entrée et ce
        que coûterait chaque panne d’un point unique, selon les hypothèses retenues. Les montants sont calculés par le
        moteur à partir de vos comptes et de ces hypothèses ; aucun ne vient d’un modèle d’IA.
      </p>
      <ReserveCalcul manques={calcul.manques} nom={nom} />
      <Fournisseurs fournisseurs={calcul.fournisseurs ?? []} nom={nom} />
      <section className="panneau" aria-labelledby="t-scen">
        <div className="panneau-tete">
          <h2 id="t-scen">Scénarios</h2>
          <span className="indice">Choisissez une ligne pour voir le chemin et le calcul</span>
        </div>
        <ReserveCourte manques={calcul.manques} />
        <div className="tableau-defile">
          <table>
            <caption className="sr">
              Scénarios d’attaque et de panne, triés par pertes potentielles estimées par sinistre (valeur haute)
            </caption>
            <thead>
              <tr>
                <th scope="col">Scénario</th>
                <th scope="col" className="num">
                  Arrêt
                </th>
                <th scope="col" className="num">
                  Pertes potentielles estimées par sinistre
                </th>
                <th scope="col" className="num">
                  Fréquence estimée par an
                </th>
                <th scope="col" className="num">
                  Pertes potentielles estimées par an
                </th>
              </tr>
            </thead>
            <tbody>
              {tries.map((s) => {
                const [cb, ch] = contributionAnnuelle(s);
                return (
                  <tr key={s.id} className="cliquable" data-choisi={s.id === scenario?.id}>
                    <td>
                      <button type="button" className="lien-btn" onClick={() => naviguer('constats', { scenario: s.id })}>
                        {s.nature === 'attaque' ? 'Attaque par' : 'Panne de'} {nom(s.entree)}
                      </button>
                      <br />
                      <small className="muted">{s.motif}</small>
                    </td>
                    <td className="num">{pourcent(s.rayon)}</td>
                    <td className="num">{fourchette(s.perte.bas.valeur, s.perte.haut.valeur)}</td>
                    <td className="num">
                      {decimal(s.frequence_annuelle[0])} à {decimal(s.frequence_annuelle[1])}
                    </td>
                    <td className="num">{fourchette(cb, ch)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row" colSpan={4}>
                  Pertes potentielles estimées (moteur), conditionnelles aux hypothèses retenues
                </th>
                <td className="num">
                  <b>{calcul.perte_annuelle_attendue
                    ? fourchette(calcul.perte_annuelle_attendue.bas.valeur, calcul.perte_annuelle_attendue.haut.valeur) : '—'}</b>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="note">
          Pertes potentielles estimées par an = somme, pour chaque scénario, de la fréquence estimée par an × les pertes
          potentielles estimées par sinistre (bas avec bas, haut avec haut). Fréquences estimées par le modèle, à calibrer selon
          le contexte ; calibration provisoire, non validée par un actuaire.{' '}
          {verif.ecart === null ? 'Le montant annuel du moteur n’est pas disponible ; la somme ne peut pas être vérifiée.'
            : Math.abs(verif.ecart[0]) < 1 && Math.abs(verif.ecart[1]) < 1
            ? 'La somme des lignes redonne exactement la valeur du moteur.'
            : `Écart entre la somme des lignes et le moteur : ${eurosLong(verif.ecart[0])} / ${eurosLong(verif.ecart[1])}.`}
        </p>
      </section>

      {scenario && <DetailScenario s={scenario} ix={ix} calcul={calcul} />}

      <section className="panneau" aria-labelledby="t-constats">
        <ReserveCalcul manques={manquesConstats ?? calcul.manques} nom={nom} />
        <div className="panneau-tete">
          <h2 id="t-constats">Constats ({constats.length})</h2>
          <span className="indice">Portés par la carte ou déduits par des règles fixes ; recalculés à chaque lecture</span>
        </div>
        <ReserveCourte manques={calcul.manques} />
        <div className="tableau-defile">
          <table>
            <caption className="sr">Constats triés par pertes potentielles estimées (valeur haute)</caption>
            <thead>
              <tr>
                <th scope="col">Constat</th>
                <th scope="col">Gravité</th>
                <th scope="col">Exigence</th>
                <th scope="col" className="num">
                  Pertes potentielles estimées si exploité
                </th>
              </tr>
            </thead>
            <tbody>
              {triConstats.map((c) => (
                <tr key={c.id} data-choisi={c.id === constat?.id}>
                  <td>
                    <button type="button" className="lien-btn" onClick={() => naviguer('constats', { constat: c.id })}>
                      {c.nom}
                    </button>
                  </td>
                  <td>
                    <EtiquetteGravite g={c.attributs?.gravite} />
                  </td>
                  <td>{c.attributs?.exigence ?? '—'}</td>
                  <td className="num">
                    {c.attributs?.perte_estimee ? fourchette(c.attributs.perte_estimee.bas.valeur, c.attributs.perte_estimee.haut.valeur) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {constat && <><ReserveCalcul manques={manquesConstats ?? calcul.manques} nom={nom} /><DetailConstat c={constat} /></>}

      <section className="panneau" aria-labelledby="t-nis2">
        <div className="panneau-tete">
          <h2 id="t-nis2">Mesures NIS2 (article 21)</h2>
          <span className="indice num" data-testid="score-nis2">
            Indicateur de progression : {libelleNis2(calcul).score} sur les mesures évaluées, {libelleNis2(calcul).detail} (pas une
            certification de conformité)
          </span>
        </div>
        <ReserveCourte manques={calcul.manques} />
        <div className="tableau-defile">
          <table>
            <thead>
              <tr>
                <th scope="col">Mesure</th>
                <th scope="col">Statut</th>
                <th scope="col">Constats</th>
              </tr>
            </thead>
            <tbody>
              {calcul.nis2.map((m) => (
                <tr key={m.id}>
                  <td>
                    <b>{m.mesure}</b> — {m.libelle}
                  </td>
                  <td>
                    <Etiquette ton={m.statut === 'conforme' ? 'ok' : m.statut === 'partiel' ? 'attention' : 'grave'}>
                      {LIBELLE_STATUT_NIS2[m.statut] ?? m.statut}
                    </Etiquette>
                  </td>
                  <td>{m.constats.join(' ; ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function DetailScenario({ s, ix, calcul }: { s: Scenario; ix: IndexCarte; calcul: Calcul }) {
  const nom = (id: string) => nomDe(ix, id);
  const p = s.detail_perte ? postes(s.detail_perte) : null;
  const verif = p ? sommePostesComptes(p.lignes) : null;
  const h = calcul.hypotheses;
  return (
    <section className="panneau apparait" aria-labelledby="t-detail" data-testid="detail-scenario">
      <LienRetour depuis="chiffre" />
      <ReserveCalcul manques={calcul.manques} nom={nom} />
      <div className="panneau-tete">
        <h2 id="t-detail">
          {s.nature === 'attaque' ? 'Attaque par' : 'Panne de'} {nom(s.entree)}
        </h2>
        <span className="ligne">
          {s.restaurable === false && <Etiquette ton="grave">Non restaurable</Etiquette>}
          {s.restaurable === true && <Etiquette ton="ok">Restaurable</Etiquette>}
          <Etiquette ton="attention">Arrêt de {pourcent(s.rayon)} de l’activité</Etiquette>
        </span>
      </div>
      <div className="grille g2">
        <div className="pile" style={{ gap: 10 }}>
          <h3>{s.nature === 'attaque' ? 'Chemin d’attaque' : 'Ce qui s’arrête'}</h3>
          {s.etapes?.length ? (
            <ol className="chemin">
              {s.etapes.map((e, i) => (
                <li key={i}>
                  <span className="pt" aria-hidden="true" />
                  <b>{e.composants.map(nom).join(', ')}</b>
                  <small>{e.motif}</small>
                </li>
              ))}
            </ol>
          ) : (
            <p>{s.motif}.</p>
          )}
          <p className="note">
            <a href={lienVers('carte', { objet: s.entree })}>Voir la porte d’entrée sur la carte</a>
          </p>
          <h3>Processus arrêtés ({s.processus_arretes.length})</h3>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {s.processus_arretes.map((id) => {
              const v = (ix.parId.get(id)?.attributs as { valeur_par_jour?: unknown } | undefined)?.valeur_par_jour;
              return (
                <li key={id}>
                  {nom(id)}
                  {v ? <span className="muted"> · {valeurLisible(v)}</span> : null}
                </li>
              );
            })}
          </ul>
        </div>
        <div className="pile" style={{ gap: 10 }}>
          <h3>Détail du calcul</h3>
          {p ? (
            <div className="tableau-defile">
              <ReserveCourte manques={calcul.manques} />
              <table>
                <caption className="sr">Postes des pertes potentielles estimées, valeur basse et valeur haute</caption>
                <thead>
                  <tr>
                    <th scope="col">Poste</th>
                    <th scope="col" className="num">
                      Bas
                    </th>
                    <th scope="col" className="num">
                      Haut
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {p.lignes.map((l) => (
                    <tr key={l.cle} className={l.compte ? '' : 'muted'}>
                      <td>{l.libelle}</td>
                      <td className="num">{eurosLong(l.bas)}</td>
                      <td className="num">{eurosLong(l.haut)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row">Pertes potentielles estimées par sinistre</th>
                    <td className="num">
                      <b>{eurosLong(p.total[0])}</b>
                    </td>
                    <td className="num">
                      <b>{eurosLong(p.total[1])}</b>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <p className="note">Le moteur n’a pas rendu de détail pour ce scénario.</p>
          )}
          {p && verif && (
            <p className="note">
              Total = somme des six premiers postes
              {Math.abs(verif[0] - p.total[0]) < 1 && Math.abs(verif[1] - p.total[1]) < 1 ? ' (vérifié)' : ' (écart à examiner)'} ; la
              trésorerie différée est montrée, pas comptée.
            </p>
          )}
          <dl className="fiche" style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 10px', margin: 0, fontSize: 14 }}>
            <dt className="muted">Fréquence estimée par le modèle, à calibrer</dt>
            <dd style={{ margin: 0 }}>
              {decimal(s.frequence_annuelle[0])} à {decimal(s.frequence_annuelle[1])} par an
              {s.vraisemblance ? ` (vraisemblance ${s.vraisemblance})` : ''}
            </dd>
            <dt className="muted">Pertes potentielles estimées par an</dt>
            <dd style={{ margin: 0 }}>{fourchette(...contributionAnnuelle(s))}</dd>
            <dt className="muted">Durées retenues</dt>
            <dd style={{ margin: 0 }}>
              {s.nature === 'attaque'
                ? s.restaurable
                  ? `restauration en ${h.restoreDays[0]} à ${h.restoreDays[1]} jours`
                  : `reconstruction en ${h.rebuildDays[0]} à ${h.rebuildDays[1]} jours`
                : `panne de ${h.outageDays[0]} à ${h.outageDays[1]} jours`}
            </dd>
            <dt className="muted">Réponse à l’incident</dt>
            <dd style={{ margin: 0 }}>
              {eurosCourt(h.responseCost[0])} à {eurosCourt(h.responseCost[1])} (hypothèse)
            </dd>
          </dl>
        </div>
      </div>
      {s.composants_compromis && (
        <details>
          <summary>Composants compromis ({s.composants_compromis.length})</summary>
          <p>{s.composants_compromis.map(nom).join(', ')}</p>
        </details>
      )}
    </section>
  );
}

function DetailConstat({ c }: { c: ObjetConstat }) {
  const { ecritureAutorisee } = useEkera();
  const a = c.attributs ?? {};
  return (
    <section className="panneau apparait" aria-labelledby="t-constat" data-testid="detail-constat">
      <div className="panneau-tete">
        <h2 id="t-constat">{c.nom}</h2>
        <EtiquetteGravite g={a.gravite} />
      </div>
      <p>{a.description}</p>
      <LienRetour depuis="constat" />
      {a.perte_estimee && <LienRetour depuis="chiffre" />}
      <dl className="fiche" style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 10px', margin: 0, fontSize: 14 }}>
        <dt className="muted">Exigence non tenue</dt>
        <dd style={{ margin: 0 }}>{a.exigence ?? '—'}</dd>
        <dt className="muted">Nature</dt>
        <dd style={{ margin: 0 }}>{a.nature ? (LIBELLE_NATURE_CONSTAT[a.nature] ?? a.nature) : '—'}</dd>
        <dt className="muted">Pertes potentielles estimées si exploité</dt>
        <dd style={{ margin: 0 }}>{a.perte_estimee ? fourchette(a.perte_estimee.bas.valeur, a.perte_estimee.haut.valeur) : '—'}</dd>
        {a.recommandation && (
          <>
            <dt className="muted">Action proposée, à valider par l’entreprise</dt>
            <dd style={{ margin: 0 }}>{a.recommandation}</dd>
          </>
        )}
      </dl>
      <BlocProvenance p={c.provenance} titre="Ce qui fonde le constat" />
      {ecritureAutorisee && (
        <div className="ligne">
          <a className="btn petit principal" href={lienVers('decisions', { depuis: c.id })}>
            Préparer une fiche de décision
          </a>
          <a className="btn petit" href={lienVers('corrections', { mode: 'travaux', constat: c.id })}>
            Proposer des travaux
          </a>
        </div>
      )}
    </section>
  );
}
